import { defineComponent } from 'vue'
import AgGridMethodsMixins from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Methods'
import AgGridColumnMethodMixins from '@/Mixins/SigmaClientNew/GeneralLedgerWorksheet/ColumnMethods'

/*
 * AG Grid Column Defs here
 */
export default defineComponent({
  mixins: [AgGridMethodsMixins, AgGridColumnMethodMixins],
  data () {
    return {
      reportGroupColumn: {
        headerName: '',
        field: 'reportGroup',
        colId: 'reportGroup',
        rowGroup: true,
        rowGroupIndex: 0,       // outer-most grouping level: GRAND TOTAL
        hide: true,
        suppressColumnsToolPanel: true,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.reportGroup : ''),
        valueSetter: () => null
      },
      groupAccountColumn: {
        headerName: '',
        field: 'groupAccount',
        colId: 'groupAccount',
        rowGroup: true,
        rowGroupIndex: 1,       // inner grouping level: account
        hide: true,
        suppressColumnsToolPanel: true,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.groupAccount : ''),
        valueSetter: () => null
      },
      accountIdColumn: {
        headerName: 'ID',
        field: 'account_id',
        colId: 'account_id',
        width: 1,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.account_id : ''),
        valueSetter: () => null
      },
      accountCodeColumn: {
        headerName: 'Account',
        field: 'account',
        colId: 'account',
        maxWidth: 300,
        minWidth: 100,
        editable: (params) => params.data ? !params.data.recon && this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.account : ''),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.account = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      accountNameColumn: {
        headerName: 'Account Name',
        field: 'account_name',
        colId: 'account_name',
        maxWidth: 400,
        minWidth: 100,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.account_name : ''),
        valueSetter: () => null
      },
      accountTypeColumn: {
        headerName: 'Type',
        field: 'type',
        colId: 'type',
        maxWidth: 150,
        editable: false,
        hide: true,
        aggFunc: 'first',
        valueGetter: (params) => {
          if (!params.data) {
            return ''
          } else {
            switch (params.data.type.toLowerCase()) {
              case 'grand_total':
                return 'GRAND TOTAL'
              default:
                return 'UNKNOWN EXPENSES'
            }
          }
        },
        valueSetter: () => null
      },
      transactionTypeColumn: {
        headerName: 'Type',
        field: 'source_code',
        colId: 'trans_type',
        maxWidth: 100,
        minWidth: 75,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.source_code : ''),
        valueSetter: () => null,
        filterValueGetter: (params) => (params.data ? params.data.source_code : '')
      },
      // Flat-mode stand-in for autoGroupColumnDef, which does not exist without
      // row-group columns: carries the checkbox and drill-down. Hidden when grouped.
      TransactionNumberColumn: {
        headerName: 'Trans #',
        field: 'trans_number',
        colId: 'trans_number',
        maxWidth: 200,
        minWidth: 100,
        hide: true,
        editable: false,
        checkboxSelection: (params) => !!params.data && !params.data.__isGroup && !params.data.__isGrandTotal,
        valueGetter: (params) => (params.data ? this.buildTransData(params.data) : ''),
        valueSetter: () => null,
        filterValueGetter: (params) => (params.data ? this.buildTransData(params.data) : null),
        cellClass: (params) => (params.data && ['PO', 'AP', 'PC', 'JE', 'PR', 'DC'].includes(params.data.trans_type) ? 'cell-blue' : ''),
        onCellClicked: (params) => this.openTransaction(params)
      },
      descriptionColumn: {
        headerName: 'GL Description',
        field: 'line_desc',
        colId: 'line_desc',
        maxWidth: 300,
        minWidth: 100,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.line_desc : ''),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.line_desc = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      vendorColumn: {
        headerName: 'Vendor',
        field: 'vendor_name',
        colId: 'vendor_name',
        maxWidth: 300,
        minWidth: 100,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.vendor_name : ''),
        valueSetter: () => null
      },
      seriesColumn: {
        headerName: 'Ser',
        field: 'series',
        colId: 'series',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.series : ''),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.series = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      locationColumn: {
        headerName: 'Loc',
        field: 'location',
        colId: 'location',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.location : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.location = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      setColumn: {
        headerName: 'Set',
        field: 'set',
        colId: 'set',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.set : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.set = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      freeField1Column: {
        headerName: 'FF1',
        field: 'free_field_1',
        colId: 'free_field_1',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.free_field_1 : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.free_field_1 = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      freeField2Column: {
        headerName: 'FF2',
        field: 'free_field_2',
        colId: 'free_field_2',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.free_field_2 : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.free_field_2 = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      freeField3Column: {
        headerName: 'FF3',
        field: 'free_field_3',
        colId: 'free_field_3',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.free_field_3 : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.free_field_3 = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      freeField4Column: {
        headerName: 'FF4',
        field: 'free_field_4',
        colId: 'free_field_4',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.free_field_4 : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.free_field_4 = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      insuranceColumn: {
        headerName: 'Ins',
        field: 'ins',
        colId: 'ins',
        maxWidth: 100,
        minWidth: 75,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.ins : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.ins = params.newValue
          return true
        }
      },
      taxCodeColumn: {
        headerName: '9',
        field: 'tax_code',
        colId: 'tax_code',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.tax_code : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.tax_code = params.newValue
          return true
        },
        cellClassRules: {
          'cell-invalid': (params) => this.isFieldInvalid(params)
        }
      },
      assetColumn: {
        headerName: 'A',
        field: 'asset',
        colId: 'asset',
        maxWidth: 100,
        minWidth: 75,
        editable: (params) => params.data ? this.isEditable(params) : false,
        valueGetter: (params) => (params.data ? params.data.asset : null),
        valueFormatter: (params) => {
          if (params.value === true || params.value === 'true') {
            return 'X'
          } else {
            return ''
          }
        },
        valueSetter: (params) => {
          params.data.edited = true
          params.data.asset = params.newValue
          return true
        }
      },
      documentNumberColumn: {
        headerName: 'Doc #',
        field: 'doc_number',
        colId: 'doc_number',
        maxWidth: 200,
        minWidth: 100,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.doc_number : null),
        valueSetter: (params) => {
          params.data.edited = true
          params.data.doc_number = params.newValue
          return true
        }
      },
      dateColumn: {
        headerName: 'Date',
        field: 'transaction_effective_date',
        colId: 'transaction_effective_date',
        maxWidth: 100,
        minWidth: 75,
        editable: false,
        valueFormatter: (params) => {
          if (params.data && params.data.transaction_effective_date) {
            const effDate = params.data.transaction_effective_date
            const effDateList = effDate.split('-')
            return effDateList[1] + '/' + effDateList[2].slice(0, 2) + '/' + effDateList[0] // MM/DD/YYYY
          }
          return null
        },
        valueGetter: (params) => (params.data && params.data.transaction_effective_date ? params.data.transaction_effective_date : null),
        valueSetter: () => null,
        filterValueGetter: (params) => {
          if (params.data && params.data.transaction_effective_date) {
            const effDate = params.data.transaction_effective_date
            const effDateList = effDate.split('-')
            return effDateList[1] + '/' + effDateList[2].slice(0, 2) + '/' + effDateList[0] // MM/DD/YYYY
          }
          return null
        }
      },
      poNumberColumn: {
        headerName: 'PO #',
        field: 'po_number',
        colId: 'po_number',
        editable: false,
        maxWidth: 200,
        minWidth: 100,
        valueGetter: (params) => (params.data ? params.data.po_number : null),
        valueSetter: () => null
      },
      paymentNumberColumn: {
        headerName: 'Check',
        field: 'payment_number',
        colId: 'payment_number',
        editable: false,
        maxWidth: 100,
        minWidth: 75,
        valueGetter: (params) => (params.data ? params.data.payment_number : null),
        valueSetter: () => null
      },
      currencyColumn: {
        headerName: 'Cur',
        field: 'transaction_currency',
        colId: 'transaction_currency',
        maxWidth: 100,
        minWidth: 75,
        editable: false,
        valueGetter: (params) => (params.data ? params.data.transaction_currency : null),
        valueSetter: () => null
      },
      amountColumn: {
        headerName: 'Amount',
        field: 'amount',
        colId: 'amount',
        cellClass: 'amount',
        maxWidth: 200,
        minWidth: 100,
        editable: false,
        valueFormatter: (params) => this.$data.scope.intlNumberFormatter(params.value || 0),
        valueGetter: (params) => (params.data ? parseFloat(params.data.amount) : 0),
        valueSetter: () => null
      }
    }
  }
})
